import { useGridApiRef } from '@mui/x-data-grid-pro';
import { POST_UNREALISED_FX_GAINS_SUCCESS } from 'adminConstants/routes';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { Row } from 'components/Layout';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import Spacing from 'components/Spacing';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  CurrencyRateDetails,
  TransactionOperation,
} from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useDisclosure } from 'further-ui/hooks';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import CashHistory from './CashHistory';

const pageId = 'post-unrealised-cash-fx-gains';

const columns: Array<Column> = [
  {
    field: 'bankName',
    headerName: 'Bank name',
    renderCell: ({ row }) => row.bankName,
    flex: 1,
    sortable: false,
  },
  {
    field: 'accountName',
    headerName: 'Account name',
    flex: 1,
    sortable: false,
  },
  {
    field: 'balanceTcy',
    headerName: 'Balance (TCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.balanceTcy, {
        fallback: '-',
        currency: row.currencyCode,
      }),
    flex: 1,
  },
  {
    field: 'originalValueLcy',
    headerName: 'Original value (LCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.calculationBreakdown?.originalValueLcy, {
        fallback: '-',
        currency: row.baseCurrency,
      }),
    flex: 1,
    sortable: false,
  },
  {
    field: 'runningTotalUnrealisedLcy',
    headerName: 'Running total unrealised',
    renderCell: ({ row }) =>
      numberToCurrencyString(
        row.calculationBreakdown?.runningTotalUnrealisedLcy,
        {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        },
      ),
    flex: 1,
    sortable: false,
  },
  {
    field: 'currentValueLcy',
    headerName: 'Current value (LCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
        fallback: '-',
        currency: row.baseCurrency,
      }),
    flex: 1,
    sortable: false,
  },
  {
    field: 'unrealisedCashFxGainValueLcy',
    headerName: 'Unrealised gain/loss (LCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.unrealisedCashFxGainValueLcy, {
        fallback: '-',
        currency: row.baseCurrency,
        negativeInBrackets: true,
      }),
    flex: 1,
    sortable: false,
  },
];

type Props = {
  vehicleId: string;
  currencyRateOverrides: Map<CurrencyCode, CurrencyRateDetails>;
  onCurrenciesLoaded: (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => void;
};

const Cash: React.FC<Props> = ({
  vehicleId,
  currencyRateOverrides,
  onCurrenciesLoaded,
}) => {
  const { vehicle } = useLayoutContext();
  const apiRef = useGridApiRef();

  const { hasEqualisationInDraftState } = useEqualisations(vehicleId);
  const confirmDialog = useDisclosure();

  const [transactionChanges, setTransactionChanges] = useState<
    Map<
      string,
      Array<{
        transactionTypeId: string;
        operationType: TransactionOperation;
      }>
    >
  >(new Map());

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data, isFetching } = api.unrealisedCashFxGainOrLoss.list.useQuery({
    vehicleId,
    ...(currencyRateOverrides
      ? { currencyRateOverrides: Object.fromEntries(currencyRateOverrides) }
      : {}),
  });

  useEffect(() => {
    if (!data?.currencyRates) return;

    onCurrenciesLoaded(data.currencyRates);
  }, [data?.currencyRates, onCurrenciesLoaded]);

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: createUnrealisedCashFXGains, isPending } =
    api.unrealisedCashFxGainOrLoss.create.useMutation(
      mutationOpts({
        successMessage: 'Created unrealised cash FX gains/losses successfully.',
        successRedirect: POST_UNREALISED_FX_GAINS_SUCCESS,
      }),
    );

  const postUnrealisedFXGains = () => {
    const postUnrealisedFXGainsBody = data?.results.map((row) => {
      const transactions = transactionChanges.get(row.id);
      return {
        bankAccountId: row.id,
        transactions: transactions ?? row.transactions,
      };
    });
    createUnrealisedCashFXGains({
      vehicleId,
      bankAccounts: postUnrealisedFXGainsBody ?? [],
      currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
    });
  };

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<{
      transactionTypeId: string;
      operationType: TransactionOperation;
    }>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });

    apiRef.current.updateRows([
      {
        id: rowId,
        transactions: newTransactions,
      },
    ]);
  };

  if (!vehicle) {
    return null;
  }

  return (
    <Spacing>
      <InteractiveTable
        apiRef={apiRef}
        id={pageId}
        columns={columns}
        rows={
          data?.results?.map((row) => ({
            ...row,
            baseCurrency: vehicle.baseCurrency,
          })) ?? []
        }
        scrollable
        loading={isFetching}
        getExpandedContent={(row) => (
          <Row fullWidth>
            {!transactionTypesLoading && (
              <TransactionAllocator
                transactionTypeOptions={transactionTypeOptions}
                transactions={row.transactions}
                onChange={(newTransactions) => {
                  handleTransactionChange(row.id ?? '', newTransactions);
                }}
                transactionCurrency={row.currencyCode}
                vehicle={vehicle}
                fxRate={row.currencyRate}
              />
            )}
          </Row>
        )}
      />
      <ButtonGroup>
        <Button
          variant="contained"
          color="primary"
          onClick={
            hasEqualisationInDraftState
              ? confirmDialog.onOpen
              : postUnrealisedFXGains
          }
          disabled={isPending}
        >
          Confirm and proceed
        </Button>
      </ButtonGroup>
      <CashHistory vehicleId={vehicleId} />
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={postUnrealisedFXGains}
          loading={isPending}
        />
      )}
    </Spacing>
  );
};

export default Cash;
